import React, { useState, useEffect } from "react";
import { Card, message, Spin } from "antd";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from "recharts";
import { api } from "../../../Services/api";
import { useSelector } from "react-redux";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const TaskBugChart = ({ projectId }) => {
	const [loading, setLoading] = useState(false);
	const [chartData, setChartData] = useState([]);
	const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));

	const trackingClientId = useSelector(
		(state) => state.Auth.user.trackingclientid
	);

	// ✅ Utility: Get Monday of any date
	function getWeekStart(date) {
		const day = date.getDay();
		const diff = day === 0 ? -6 : 1 - day; // Monday as first day
		const monday = new Date(date);
		monday.setDate(date.getDate() + diff);
		monday.setHours(0, 0, 0, 0);
		return monday;
	}

	// ✅ Utility: Format date as YYYY-MM-DD
	const formatDate = (date) => date.toISOString().split("T")[0];

	// ✅ Get date range for current week
	const getDateRange = () => {
		const startDate = new Date(currentWeekStart);
		const endDate = new Date(startDate);
		endDate.setDate(startDate.getDate() + 6);
		return { startdate: formatDate(startDate), enddate: formatDate(endDate) };
	};

	// ✅ Format week label like "Date: 01 Nov 27 - 07 Nov 27"
	const formatWeekLabel = () => {
		const start = new Date(currentWeekStart);
		const end = new Date(currentWeekStart);
		end.setDate(start.getDate() + 6);

		const formatDay = (date) => {
			const day = String(date.getDate()).padStart(2, "0");
			const month = date.toLocaleString("en-US", { month: "short" });
			const year = String(date.getFullYear()).slice(-2); // 2027 -> 27
			return `${day} ${month} ${year}`;
		};

		return `Date: ${formatDay(start)} - ${formatDay(end)}`;
	};

	// ✅ Fetch data from API for current week
	const fetchTaskData = async () => {
		setLoading(true);
		try {
			const { startdate, enddate } = getDateRange();
			const payload = {
				trackingclientid: trackingClientId,
				projectid: projectId,
				startdate,
				enddate,
			};

			const response = await api.post("taskpercentage/gettaskdata", payload, {
				headers: { authorizationToken: localStorage.getItem("token") },
			});

			const data = response.data;

			if (data?.taskTypeSummary?.length) {
				const grouped = data.taskTypeSummary.reduce((acc, item) => {
					const isBug = item.label.toLowerCase().includes("bug");
					const label = item.label || "Unknown";

					if (!acc[label]) acc[label] = { name: label, tasks: 0, bugs: 0 };
					if (isBug) acc[label].bugs += item.count;
					else acc[label].tasks += item.count;
					return acc;
				}, {});
				setChartData(Object.values(grouped));
			} else {
				setChartData([]);
			}
		} catch (error) {
			console.error("Error fetching task data:", error);
			// message.error("Failed to load task data");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchTaskData();
	}, [currentWeekStart]);

	// ✅ Handle week navigation
	const goToPreviousWeek = () => {
		const newWeek = new Date(currentWeekStart);
		newWeek.setDate(currentWeekStart.getDate() - 7);
		setCurrentWeekStart(getWeekStart(newWeek));
	};

	const goToNextWeek = () => {
		const newWeek = new Date(currentWeekStart);
		newWeek.setDate(currentWeekStart.getDate() + 7);
		const nextMonday = getWeekStart(newWeek);
		if (nextMonday > getWeekStart(new Date())) return; // prevent future weeks
		setCurrentWeekStart(nextMonday);
	};

	const isFutureWeek =
		getWeekStart(currentWeekStart).getTime() >= getWeekStart(new Date()).getTime();

	return (
		<Card
			title={<span style={{ color: "#fff" }}>Tasks Overview</span>}
			bordered={false}
			style={{
				background: "#3E3F3E",
				borderRadius: 10,
				color: "#fff",
				boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
			}}
			extra={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "15px",
						color: "#fff",
					}}
				>
					<LeftOutlined
						onClick={goToPreviousWeek}
						style={{ cursor: "pointer", fontSize: 18 }}
					/>
					<span
						style={{
							fontWeight: 500,
							minWidth: 220,
							textAlign: "center",
							letterSpacing: 0.5,
						}}
					>
						{formatWeekLabel()}
					</span>
					<RightOutlined
						onClick={goToNextWeek}
						style={{
							cursor: isFutureWeek ? "not-allowed" : "pointer",
							opacity: isFutureWeek ? 0.5 : 1,
							fontSize: 18,
						}}
					/>
				</div>
			}
		>
			<div
				style={{
					height: 320, // fixed consistent height for the chart area
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				{loading ? (
					<Spin size="large" />
				) : chartData?.length > 0 ? (
					<ResponsiveContainer width="100%" height="100%">
						<BarChart
							data={chartData}
							barCategoryGap="40%"
							margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
						>
							<defs>
								<linearGradient id="colorTasks" x1="0" y1="0" x2="0" y2="1">
									<stop offset="0%" stopColor="#00B4D8" stopOpacity={0.9} />
									<stop offset="100%" stopColor="#90E0EF" stopOpacity={0.7} />
								</linearGradient>

								<linearGradient id="colorBugs" x1="0" y1="0" x2="0" y2="1">
									<stop offset="0%" stopColor="#ff0000ff" stopOpacity={0.9} />
									<stop offset="100%" stopColor="#FFB347" stopOpacity={0.7} />
								</linearGradient>
							</defs>

							<CartesianGrid stroke="rgba(255,255,255,0.2)" strokeDasharray="6 6" />
							<XAxis dataKey="name" tick={{ fill: "#fff" }} />
							<YAxis tick={{ fill: "#fff" }} />
							<Tooltip
								cursor={{ fill: "#c9c7c7ff" }}
								content={({ payload }) => {
									if (!payload || payload?.length === 0) return null;

									// Prefer the payload item that has a non-zero value (the hovered one).
									// If none found, fall back to the max value item, then payload[0].
									const nonZero = payload.find((p) => p && p.value !== undefined && p.value !== 0);
									const maxVal = payload.slice().sort((a, b) => (b.value || 0) - (a.value || 0))[0];
									const item = nonZero || maxVal || payload[0];

									// resolve a nice display name
									const displayName = (() => {
										const raw = (item && (item.name || item.dataKey || item.payload?.name)) || "";
										if (/bug/i.test(raw)) return "Bugs / Issue";
										if (/task|rfc|request/i.test(raw)) return "New Task / RFC";
										return raw;
									})();

									return (
										<div
											style={{
												backgroundColor: "#2f2f2f",
												padding: "8px 12px",
												borderRadius: "8px",
												border: "1px solid rgba(255,255,255,0.15)",
												color: "#fff",
												fontSize: 13,
												boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
												minWidth: 120,
											}}
										>
											<div style={{ fontSize: 12, marginBottom: 6, color: "#ccc" }}>{item.payload?.name}</div>
											<div style={{ fontWeight: 600, color: "#fff" }}>
												{displayName} : {item.value}
											</div>
										</div>
									);
								}}
							/>



							<Legend wrapperStyle={{ color: "#fff" }} />
							<Bar
								dataKey="tasks"
								name="New Task / RFC"
								fill="url(#colorTasks)"
								barSize={40}
								radius={[6, 6, 0, 0]}
								animationDuration={1000}
								animationEasing="ease-in-out"
							/>
							<Bar
								dataKey="bugs"
								name="Bugs / Issue"
								fill="url(#colorBugs)"
								barSize={40}
								radius={[6, 6, 0, 0]}
								animationDuration={1000}
								animationEasing="ease-in-out"
							/>
						</BarChart>
					</ResponsiveContainer>
				) : (
					<div style={{ textAlign: "center", color: "#ccc", fontSize: 15 }}>
						No task or bug data available for this week
					</div>
				)}
			</div>


		</Card>
	);
};

export default TaskBugChart;
